/* global SideDrawer */

if (!customElements.get('facet-filters')) {
  class FacetFilters extends SideDrawer {
    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.historyChangeHandler);

      if (this.breakpointChangeHandler) {
        window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }
    }

    init() {
      this.filteringEnabled = this.dataset.filtering === 'true';
      this.sortingEnabled = this.dataset.sorting === 'true';
      this.form = document.getElementById('facets');
      this.results = document.getElementById('filter-results');
      this.expanded = [];
      this.filterChangeTimeout = null;
      this.dynamicCategoryOptions = this.dataset.dynamicCategoryOptions === 'true';

      this.categoryFilterBaseline = null;
      this.categoryFilterInteracted = false;
      this.categoryFilterBaselinePromise = this.loadCategoryFilterBaseline();

      this.handleBreakpointChange();
      this.addElements();
      this.addListeners();
      this.updatePriceRangeMaximum(new URLSearchParams(window.location.search));

      this.historyChangeHandler = this.historyChangeHandler || this.handleHistoryChange.bind(this);
      window.addEventListener('popstate', this.historyChangeHandler);
    }

    addElements() {
      if (this.filteringEnabled) {
        this.filters = this.querySelector('.facets__filters');
        this.activeFilters = this.querySelector('.facets__active-filters');
        this.activeFiltersList = this.querySelector('.active-filters');
        this.activeFiltersHeader = this.querySelector('.active-filters-header');
        this.footer = this.querySelector('.facets__footer');
      }

      if (this.sortingEnabled) {
        this.mobileSortByOptions = this.querySelectorAll('.js-drawer-sort-by');
        this.desktopSortBy = document.querySelector('.products-toolbar__sort');
      }
    }

    addListeners() {
      if (this.filteringEnabled) {
        this.breakpointChangeHandler = this.breakpointChangeHandler
          || this.handleBreakpointChange.bind(this);
        this.filters.addEventListener('click', this.handleFiltersClick.bind(this));
        this.filters.addEventListener('input', this.handleFilterChange.bind(this));
        this.filters.addEventListener('change', this.handleFilterChange.bind(this));
        this.activeFilters.addEventListener('click', this.handleActiveFiltersClick.bind(this));
        window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }

      if (this.sortingEnabled) {
        this.desktopSortBy.addEventListener('change', this.handleFilterChange.bind(this));
      }
    }

    /**
     * Handles viewport breakpoint changes.
     */
    handleBreakpointChange() {
      if (theme.mediaMatches.lg) {
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        this.removeAttribute('aria-modal');
        this.removeAttribute('role');
      } else {
        this.close();
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-hidden', 'true');
        this.hidden = false;
      }
    }

    /**
     * Handles 'input' events on the filters and 'change' events on the sort by dropdown.
     * @param {object} evt - Event object.
     */
    handleFilterChange(evt) {
      // Only allow price 'change' events
      if (evt.type === 'change' && !(evt.target.id?.includes('price-range') || evt.target.id?.includes('sort-by'))) return;

      // Dont reload when typing a price
      if (evt.target.id?.includes('price-range') && evt.constructor.name === 'InputEvent') return;

      if (evt.target.closest('[data-category-menu-filter]')) this.categoryFilterInteracted = true;

      const timeoutDelay = 500;

      clearTimeout(this.filterChangeTimeout);

      this.filterChangeTimeout = setTimeout(() => {
        const formData = new FormData(this.form);
        const searchParams = new URLSearchParams(formData);
        const emptyParams = [];

        if (this.sortingEnabled) {
          let currentSortBy = searchParams.get('sort_by');

          // Keep the mobile facets form sync'd with the desktop sort by dropdown
          if (evt.target.tagName === 'CUSTOM-SELECT') {
            this.mobileSortByOptions.forEach((option) => {
              option.checked = option.value === evt.detail.selectedValue;
              currentSortBy = evt.detail.selectedValue;
            });
          }

          // Set the 'sort_by' parameter.
          searchParams.set('sort_by', currentSortBy);
        }

        // Get empty parameters.
        searchParams.forEach((value, key) => {
          if (!value) emptyParams.push(key);
        });

        // Remove empty parameters.
        emptyParams.forEach((key) => {
          searchParams.delete(key);
        });

        this.setDefaultInStockState(false);

        this.applyFilters(searchParams.toString(), evt);
      }, timeoutDelay);
    }

    /**
     * Handles 'click' events on the filters.
     * @param {object} evt - Event object.
     */
    handleFiltersClick(evt) {
      const { target } = evt;

      // Filter 'clear' button clicked.
      if (target.matches('.js-clear-filter')) {
        evt.preventDefault();
        const searchParams = new URL(evt.target.href).searchParams;
        this.setDefaultInStockState(false);
        this.applyFilters(searchParams.toString(), evt);
      }

      // Filter 'show more' button clicked.
      if (target.matches('.js-show-more')) {
        const filter = target.closest('.filter');
        target.remove();

        filter.querySelectorAll('li').forEach((el) => {
          el.classList.remove('js-hidden');
        });

        if (!this.expanded.includes(filter.id)) {
          this.expanded.push(filter.id);
        }
      }
    }

    /**
     * Handles 'click' events on the active filters.
     * @param {object} evt - Event object.
     */
    handleActiveFiltersClick(evt) {
      if (evt.target.tagName !== 'A') return;
      evt.preventDefault();
      const searchParams = new URL(evt.target.href).searchParams;
      this.setDefaultInStockState(false);
      this.applyFilters(searchParams.toString(), evt);
    }

    /**
     * Toggles the no-fetch default in-stock view for the current rendered result list.
     * @param {boolean} active - Whether unavailable product cards should be hidden.
     */
    setDefaultInStockState(active) {
      const customPagination = document.querySelector('custom-pagination');
      if (customPagination && this.dataset.defaultInStockFilter === 'true') {
        this.defaultInStockActive = active;
        customPagination.dataset.defaultInStockActive = String(active);
      }
    }

    /**
     * Keeps the synthetic default in-stock checkbox in sync after filter HTML is re-rendered.
     * @param {URLSearchParams} searchParams - Filter/sort parameters that were just applied.
     */
    syncDefaultInStockControl(searchParams) {
      if (this.dataset.defaultInStockFilter !== 'true' || searchParams.has('filter.v.availability')) return;

      const inStockInput = this.form?.querySelector('[name="filter.v.availability"][value="1"]');
      if (inStockInput) inStockInput.checked = this.defaultInStockActive !== false;
    }

    /**
     * Replaces Shopify's collection-wide price ceiling with the highest price
     * among products matching the active non-price filters.
     * @param {URLSearchParams} searchParams - Currently applied filter parameters.
     * @param {DocumentFragment} [renderedContent] - Already fetched section markup.
     */
    async updatePriceRangeMaximum(searchParams, renderedContent = null) {
      if (!this.form?.dataset.filterSectionId) return;

      const nonPriceFilterKeys = [...searchParams.keys()].filter((key) => (
        key.startsWith('filter.') && key !== 'filter.v.price.gte' && key !== 'filter.v.price.lte'
      ));
      if (!nonPriceFilterKeys.length) return;

      let content = renderedContent;
      const isPriceDescending = searchParams.get('sort_by') === 'price-descending';

      if (!content || !isPriceDescending) {
        const maximumParams = new URLSearchParams(searchParams);
        maximumParams.delete('filter.v.price.gte');
        maximumParams.delete('filter.v.price.lte');
        maximumParams.set('sort_by', 'price-descending');
        maximumParams.set('section_id', this.form.dataset.filterSectionId);

        if (this.priceMaximumFetchAbortController) {
          this.priceMaximumFetchAbortController.abort('Request changed');
        }
        this.priceMaximumFetchAbortController = new AbortController();

        try {
          const response = await fetch(`${window.location.pathname}?${maximumParams}`, {
            method: 'GET',
            signal: this.priceMaximumFetchAbortController.signal
          });
          if (!response.ok) return;

          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();
          content = tmpl.content;
        } catch (error) {
          if (error.name !== 'AbortError') console.warn(error); // eslint-disable-line
          return;
        }
      }

      const prices = [...content.querySelectorAll('#filter-results [data-product-price-max]')]
        .map((product) => Number(product.dataset.productPriceMax))
        .filter(Number.isFinite);
      if (!prices.length) return;

      const maximum = Math.ceil(Math.max(...prices) / 100);
      const priceRange = this.form.querySelector('price-range');
      const maximumNumberInput = priceRange?.querySelector('#price-range-number-max');
      const maximumSliderInput = priceRange?.querySelector('#price-range-slider-max');
      if (!maximumNumberInput || !maximumSliderInput || maximum <= Number(maximumNumberInput.min)) return;

      maximumNumberInput.max = maximum;
      maximumNumberInput.placeholder = maximum;
      maximumSliderInput.max = maximum;

      if (!maximumNumberInput.value || Number(maximumNumberInput.value) > maximum) {
        maximumNumberInput.value = '';
        maximumSliderInput.value = maximum;
      }
      priceRange.maxValue = maximum;
    }

    /** Keep the menu's category choices from the unfiltered collection. */
    async loadCategoryFilterBaseline() {
      if (this.dynamicCategoryOptions) return;

      const currentParams = new URLSearchParams(window.location.search);
      const hasActiveFilters = [...currentParams.keys()].some((key) => key.startsWith('filter.'));

      if (!hasActiveFilters) {
        const categoryFilter = this.form.querySelector('[data-category-menu-filter]')?.closest('details-disclosure');
        if (categoryFilter) {
          this.categoryFilterBaselineIndex = [...categoryFilter.parentElement.children].indexOf(categoryFilter);
          this.categoryFilterBaseline = categoryFilter.cloneNode(true);
        }
        return;
      }

      if (!this.form.dataset.filterSectionId) return;

      try {
        const url = `${window.location.pathname}?section_id=${encodeURIComponent(this.form.dataset.filterSectionId)}`;
        const response = await fetch(url);
        if (!response.ok) return;

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();
        const categoryFilter = tmpl.content.querySelector('#facets [data-category-menu-filter]')?.closest('details-disclosure');
        if (categoryFilter) {
          this.categoryFilterBaselineIndex = [...categoryFilter.parentElement.children].indexOf(categoryFilter);
          this.categoryFilterBaseline = categoryFilter.cloneNode(true);
          const selectedParams = this.categoryFilterInteracted
            ? new URLSearchParams(new FormData(this.form))
            : new URLSearchParams(window.location.search);
          this.restoreCategoryOptions(this.form, selectedParams);
        }
      } catch (error) {
        console.warn(error); // eslint-disable-line
      }
    }

    /** Restore every category option and select the values in the current URL. */
    restoreCategoryOptions(form, searchParams) {
      if (this.dynamicCategoryOptions) return;
      if (!this.categoryFilterBaseline) return;

      const baselineFilter = this.categoryFilterBaseline.querySelector('[data-category-menu-filter]');
      const currentFilter = form.querySelector('[data-category-menu-filter]');
      const replacement = this.categoryFilterBaseline.cloneNode(true);
      const replacementFilter = replacement.querySelector('[data-category-menu-filter]');
      replacementFilter.open = currentFilter?.open ?? baselineFilter.open;

      replacement.querySelectorAll('input[type="checkbox"]').forEach((input) => {
        input.defaultChecked = searchParams.getAll(input.name)
          .flatMap((value) => value.split(','))
          .includes(input.value);
      });

      const clearLink = replacement.querySelector('.js-clear-filter');
      const categoryParams = [...replacement.querySelectorAll('input[type="checkbox"]')]
        .map((input) => input.name)
        .filter((name, index, names) => name && names.indexOf(name) === index);
      if (clearLink && categoryParams.length) {
        const clearParams = new URLSearchParams(searchParams);
        categoryParams.forEach((name) => clearParams.delete(name));
        clearLink.href = `${window.location.pathname}${clearParams.size ? `?${clearParams}` : ''}`;
        clearLink.hidden = !categoryParams.some((name) => searchParams.has(name));
      }

      if (currentFilter) {
        currentFilter.closest('details-disclosure').replaceWith(replacement);
      } else {
        const filters = form.querySelector('.facets__filters');
        filters?.insertBefore(replacement, filters.children[this.categoryFilterBaselineIndex] || null);
      }
    }

    /**
     * Handles history changes (e.g. back button clicked).
     * @param {object} evt - Event object.
     */
    handleHistoryChange(evt) {
      if (evt.state !== null) {
        let searchParams = '';

        if (evt.state && evt.state.searchParams) {
          ({ searchParams } = evt.state);
        }

        this.applyFilters(searchParams, null, false);
      }
    }

    /**
     * Fetches the filtered/sorted page data and updates the current page.
     * @param {string} searchParams - Filter/sort search parameters.
     * @param {object} evt - Event object.
     * @param {boolean} [updateUrl=true] - Update url with the selected options.
     */
    async applyFilters(searchParams, evt, updateUrl = true) {
      try {
        const appliedSearchParams = new URLSearchParams(searchParams);

        // Preserve the current element focus
        const activeElementId = document.activeElement.id;

        // Disable infinite scrolling.
        const customPagination = document.querySelector('custom-pagination');
        if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'true';

        // Set loading state.
        this.results.classList.add('is-loading');

        // Disable "Show X results" button until submission is complete.
        const closeBtn = this.querySelector('.js-close-drawer-mob');
        closeBtn.ariaDisabled = 'true';
        closeBtn.classList.add('is-loading');

        // Use Section Rendering API for the request, if possible.
        let fetchUrl = `${window.location.pathname}?${searchParams}`;
        if (this.form.dataset.filterSectionId) {
          fetchUrl += `&section_id=${this.form.dataset.filterSectionId}`;
        }

        // Cancel current fetch request. (Raises an exception)
        if (this.applyFiltersFetchAbortController) {
          this.applyFiltersFetchAbortController.abort('Request changed');
        }
        this.applyFiltersFetchAbortController = new AbortController();

        // Fetch filtered products markup.
        const response = await fetch(fetchUrl, {
          method: 'GET',
          signal: this.applyFiltersFetchAbortController.signal
        });

        if (response.ok) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();

          await this.categoryFilterBaselinePromise;
          this.restoreCategoryOptions(tmpl.content.getElementById('facets'), appliedSearchParams);

          // Restore UI state.
          this.form.querySelectorAll('details-disclosure > details').forEach((existingFilter) => {
            const target = tmpl.content.getElementById(existingFilter.id);
            if (target) {
              target.open = existingFilter.open;
            }
          });
          tmpl.content.querySelectorAll('#facets details-disclosure > details').forEach((newFilter) => {
            if (this.expanded.includes(newFilter.id)) {
              const hiddenElements = newFilter.querySelectorAll('.js-hidden');
              hiddenElements.forEach((listItem) => {
                listItem.classList.remove('js-hidden');
              });
              newFilter.querySelector('.filter__more')?.remove();
            }
          });

          // Update the filters.
          this.form.innerHTML = tmpl.content.getElementById('facets').innerHTML;

          // Update the label of the mobile filter button
          closeBtn.innerText = tmpl.content.querySelector('.js-close-drawer-mob').innerText;

          // Preserve the CSS class of the results
          const currentResultsUl = this.results.querySelector('ul');
          this.currentResultsClass = currentResultsUl ? this.results.querySelector('ul').getAttribute('class') : this.currentResultsClass;

          // Update the results.
          this.results.innerHTML = tmpl.content.getElementById('filter-results').innerHTML;

          // Reuse the count returned with the filtered section; no extra request or card counting needed.
          const filteredContainer = tmpl.content.querySelector('[data-collection-count-label]');
          const collectionCount = document.querySelector('[data-collection-product-count]');
          if (filteredContainer && collectionCount) {
            collectionCount.textContent = filteredContainer.dataset.collectionCountLabel;
          }

          // Set the CSS class of the results to what it was
          const newResultsUl = this.results.querySelector('ul');
          if (newResultsUl && this.currentResultsClass) newResultsUl.setAttribute('class', this.currentResultsClass);

          // Reinitialize re-rendered components.
          this.addElements();
          this.addListeners();
          this.syncDefaultInStockControl(appliedSearchParams);
          await this.updatePriceRangeMaximum(appliedSearchParams, tmpl.content);

          // Reinitialize any custom pagination
          if (customPagination && customPagination.reload) customPagination.reload();

          // Update the URL.
          if (updateUrl) FacetFilters.updateURL(searchParams);

          // Scroll to the top of the results if needed
          if (this.results.getBoundingClientRect().top < 0) {
            // If the header is sticky, compensate for it when scrolling to elements
            let headerHeight = 0;
            if (document.querySelector('store-header[data-is-sticky="true"]')) {
              headerHeight = Number.parseInt(
                getComputedStyle(this.parentElement)
                  .getPropertyValue('--header-height')
                  .replace('px', ''),
                10
              ) || 0;
            }
            window.scrollTo({
              top: this.results.getBoundingClientRect().top + window.scrollY - headerHeight - 45,
              behavior: 'smooth'
            });
          }

          // Enable the "Show X results" button
          closeBtn.classList.remove('is-loading');
          closeBtn.removeAttribute('aria-disabled');

          // Renable infinite scroll
          if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'false';

          // Focus on the element with the same ID in the new HTML
          if (activeElementId) document.getElementById(activeElementId)?.focus();

          // Broadcast the update for anything else to hook into
          document.dispatchEvent(new CustomEvent('on:facet-filters:updated'), { bubbles: true });
        }
      } catch (error) {
        console.warn(error); // eslint-disable-line
      } finally {
        this.results.classList.remove('is-loading');
      }
    }

    /**
     * Updates the url with the current filter/sort parameters.
     * @param {string} searchParams - Filter/sort parameters.
     */
    static updateURL(searchParams) {
      window.history.pushState(
        { searchParams },
        '',
        `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
      );
    }
  }

  customElements.define('facet-filters', FacetFilters);
}
